/*
 * @Author: Bing Ni nibing1220@qq.com
 * @Date: 2024-08-09 11:07:16
 * @LastEditors: Bing Ni nibing1220@qq.com
 * @LastEditTime: 2024-08-09 11:10:23
 * @FilePath: /fleximagegenerator/src/components/login/ButtonAccount.js
 * @description: 用户账户按钮组件，显示用户头像和下拉菜单，提供账户相关功能
 */
/* eslint-disable @next/next/no-img-element */
"use client";
import { useRouter } from "next/navigation";
import { UserContext } from "@/context/UserContext";
import { useState, useEffect, useContext } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { createClient } from '@/utils/supabase/client';
import apiClient from "@/libs/api";
import { cn } from "@/libs/utils";
import LoginModal from "@/components/modals/LoginModal";

/**
 * 头像和用户名组件
 * 
 * @param {Object} supabaseUser - Supabase 用户对象
 * @param {boolean} avatarOnly - 是否只显示头像
 * @param {boolean} isGuest - 是否为访客用户
 * @returns {JSX.Element} - 返回头像和用户名组件
 */
const AvatarAndName = ({ supabaseUser, avatarOnly = false, isGuest = false }) => {
  return (
    <div className="flex justify-center items-center gap-2">
      {isGuest ? (
        // 如果是访客用户，显示访客图标
        <span className="w-7 h-7 bg-gradient-to-br from-gray-500/30 to-gray-700/30 flex justify-center items-center rounded-full shrink-0 capitalize ring-1 ring-green-500/20 hover:ring-green-400/50 transition-all duration-300 text-white shadow-[0_0_6px_rgba(168,85,247,0.15)] text-sm">
          <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        </span>
      ) : supabaseUser?.user_metadata?.avatar_url ? (
        // 如果用户有头像，显示头像
        <img
          src={supabaseUser?.user_metadata?.avatar_url}
          alt={"Profile picture"}
          className="w-7 h-7 rounded-full bg-gray-50 shrink-0 ring-1 ring-green-500/40 hover:ring-green-400/70 transition-all duration-300 shadow-[0_0_6px_rgba(168,85,247,0.25)]"
          referrerPolicy="no-referrer"
          width={28}
          height={28}
        />
      ) : (
        // 如果用户没有头像，显示邮箱首字母作为头像
        <span className="w-7 h-7 bg-gradient-to-br from-green-500/30 to-cyan-500/30 flex justify-center items-center rounded-full shrink-0 capitalize ring-1 ring-green-500/40 hover:ring-green-400/70 transition-all duration-300 text-white shadow-[0_0_6px_rgba(168,85,247,0.2)] text-sm">
          {supabaseUser?.email?.charAt(0)}
        </span>
      )}

      {!avatarOnly && (
        // 如果不是只显示头像，则显示用户名
        <span className="text-xs font-medium bg-gradient-to-r from-green-300 to-cyan-300 text-transparent bg-clip-text">
          {isGuest ? "Guest" : supabaseUser?.user_metadata?.name || supabaseUser?.email?.split("@")[0] || "Account"}
        </span>
      )}
    </div>
  );
};

/**
 * 账户按钮组件 - 显示用户头像和下拉菜单
 * 
 * @param {Object} supabaseUser - Supabase 用户对象
 * @param {boolean} mobileStyle - 是否使用移动端样式
 * @param {boolean} avatarOnly - 是否只显示头像
 * @param {boolean} fullWidth - 按钮是否占满容器宽度
 * @param {string} className - 自定义类名
 * @returns {JSX.Element} - 返回账户按钮组件
 */
const ButtonAccount = ({ 
  supabaseUser, 
  mobileStyle = false, 
  avatarOnly = false,
  fullWidth = false,
  className = "" 
}) => {
  // 创建 Supabase 客户端实例
  const supabase = createClient();
  // 加载状态
  const [isLoading, setIsLoading] = useState(false);
  const route = useRouter();
  // 从 UserContext 获取用户信息
  const { user } = useContext(UserContext);
  // 登录模态框状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  // 获取当前路径
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  // 判断是否为访客用户
  const isGuest = user?.is_guest === true;
  // console.log("userInfo: ", user);  // 调试信息

  /**
   * 处理登出逻辑
   * 从 Supabase 登出并重定向到首页
   */
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  /**
   * 处理账单管理逻辑
   * 创建 Stripe 支付门户会话并重定向
   */
  const handleBilling = async () => {
    setIsLoading(true);

    try {
      // 调用 API 创建 Stripe 门户会话
      const { url } = await apiClient.post("/stripe/create-portal", {
        returnUrl: window.location.href,
      });

      // 重定向到 Stripe 门户
      window.location.href = url;
    } catch (e) {
      console.error(e);
    }

    setIsLoading(false);
  };

  /**
   * 处理登录按钮点击
   * 打开登录模态框
   */
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  return (
    <>
      <Popover className={cn("relative z-10", fullWidth && "w-full")}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            className={cn(
              "transition-all duration-300 hover:bg-gradient-to-r hover:from-green-500/10 hover:to-cyan-500/10 border border-transparent hover:border-green-500/20",
              !avatarOnly && "hover:-translate-y-0.5 p-2",
              avatarOnly ? "p-0 h-auto w-auto" : "p-2 rounded-lg",
              fullWidth && "w-full justify-between",
              className
            )}
            aria-label={`用户菜单 - ${isGuest ? "访客" : supabaseUser?.user_metadata?.name || supabaseUser?.email?.split("@")[0] || "账户"}`}
            aria-haspopup="true"
            aria-expanded="false"
          >
            {/* 显示头像和用户名组件 */}
            <AvatarAndName supabaseUser={supabaseUser} avatarOnly={avatarOnly} isGuest={isGuest} />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-56 p-2 bg-black/95 border border-green-500/30 shadow-lg shadow-green-900/20 animate-in fade-in-0 zoom-in-95 backdrop-blur-lg rounded-lg"
          align={avatarOnly ? "end" : "center"}
          role="menu"
          aria-label="用户账户菜单"
        >
          {/* 下拉菜单内容 */}
          <div className="overflow-hidden rounded-lg">
            <div className="space-y-1.5">
              {/* 显示剩余积分 */}
              <div
                className="flex flex-col gap-1 bg-gradient-to-br from-green-900/20 to-cyan-900/20 backdrop-blur-md transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium border border-green-500/20 shadow-[0_0_8px_rgba(168,85,247,0.15)]"
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-xs uppercase tracking-wider">Credits Balance</span>
                  <button 
                    onClick={() => route.push("/pricing")}
                    className="text-xs text-green-400 hover:text-green-300 transition-colors duration-300 hover:underline"
                    aria-label="购买更多积分"
                  >
                    Get More
                  </button>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text">{user?.left_credits || 0}</span>
                  <span className="text-xs text-gray-400">credits remaining</span>
                </div>
              </div>

              {/* 所有用户 - 历史作品按钮 */}
              <button
                className="flex items-center gap-2 hover:bg-gradient-to-r hover:from-green-500/10 hover:to-cyan-500/10 transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium group text-white border border-transparent hover:border-green-500/20"
                onClick={() => route.push("/history")}
                role="menuitem"
                aria-label="查看我的创作历史"
              >
                <div className="w-6 h-6 rounded-full bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-3 h-3 text-green-400 group-hover:text-green-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                  </svg>
                </div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="group-hover:translate-x-0.5 transition-transform duration-300 whitespace-nowrap">My Creations</span>
                </div>
              </button>

              {isGuest ? (
                <>
                  {/* 访客用户 - 登录按钮 */}
                  <button
                    className="flex items-center gap-2 bg-gradient-to-r from-green-400 to-cyan-400 hover:from-green-500 hover:to-cyan-500 transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium group text-white border border-transparent hover:shadow-[0_0_15px_rgba(168,85,247,0.4)]"
                    onClick={handleLoginClick}
                    role="menuitem"
                    aria-label="登录到您的账户"
                  >
                    <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-3 h-3 text-white group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9l3 3m0 0l-3 3m3-3H2.25" />
                      </svg>
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="group-hover:translate-x-0.5 transition-transform duration-300 whitespace-nowrap">Login to Your Account</span>
                    </div>
                  </button>

                  {/* 访客用户 - 查看价格按钮 */}
                  <button
                    className="flex items-center gap-2 hover:bg-gradient-to-r hover:from-green-500/10 hover:to-cyan-500/10 transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium group text-white border border-transparent hover:border-green-500/20"
                    onClick={() => route.push("/pricing")}
                    role="menuitem"
                    aria-label="查看价格计划"
                  >
                    <div className="w-6 h-6 rounded-full bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-3 h-3 text-green-400 group-hover:text-green-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 01-.75.75h-.75m-6-1.5H2.25m19.5 0v.75c0 .414-.336.75-.75.75h-.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 01-.75.75h-.75" />
                      </svg>
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="group-hover:translate-x-0.5 transition-transform duration-300 whitespace-nowrap">View Pricing Plans</span>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  {/* 正常用户 - 账单管理按钮 */}
                  <button
                    className="flex items-center gap-2 hover:bg-gradient-to-r hover:from-green-500/10 hover:to-cyan-500/10 transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium group text-white border border-transparent hover:border-green-500/20"
                    onClick={() => route.push("/billing")}
                    role="menuitem"
                    aria-label="管理账单和订阅"
                  >
                    <div className="w-6 h-6 rounded-full bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-3 h-3 text-green-400 group-hover:text-green-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                      </svg>
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="group-hover:translate-x-0.5 transition-transform duration-300 whitespace-nowrap">Billing</span>
                    </div>
                  </button>

                  {/* 正常用户 - 登出按钮 */}
                  <button
                    className="flex items-center gap-2 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 py-2 px-3 w-full rounded-lg text-xs font-medium group text-white border border-transparent hover:border-red-500/20"
                    onClick={handleSignOut}
                    role="menuitem"
                    aria-label="退出登录"
                  >
                    <div className="w-6 h-6 rounded-full bg-red-900/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-3 h-3 text-red-400 group-hover:text-red-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                      </svg>
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="group-hover:translate-x-0.5 transition-transform duration-300 whitespace-nowrap">Logout</span>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* 登录模态框 */}
      {showLoginModal && (
        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => setShowLoginModal(false)}
          redirectTo={currentPath}
          onSuccess={() => {
            setShowLoginModal(false);
          }}
        />
      )}
    </>
  );
};

export default ButtonAccount;
